import { cn } from "./classNames-BzrkDnOD.js";
import { useArgoDesign } from "./context-U293r3v-.js";
import { AlertTriangle, CircleCheck, Info } from "./icons-CXmDd4VN.js";
import { Button } from "./Button-uWJf4kxd.js";
import { createContext, use, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
import * as AlertDialogPrimitive from "@radix-ui/react-alert-dialog";
import { Action, Cancel, Content, Overlay, Portal, Root, Title, Trigger } from "@radix-ui/react-alert-dialog";
//#region src/components/AlertModal/AlertModalDescription.tsx
const AlertModalDescription = ({ className, ...props }) => /* @__PURE__ */ jsx(AlertDialogPrimitive.Description, {
	className: cn("tw:font-sans", "tw:tracking-normal", "tw:font-normal", "tw:text-muted-foreground", "tw:group-data-[design=default]/alert-modal:text-foreground", "tw:group-data-[design=default]/alert-modal:text-sm", className),
	...props
});
//#endregion
//#region src/components/AlertModal/AlertModalFooter.tsx
const AlertModalFooter = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	className: cn("tw:flex", "tw:gap-2", "tw:group-data-[design=default]/alert-modal:flex-col", "tw:group-data-[design=default]/alert-modal:sm:flex-row", "tw:group-data-[design=argo]/alert-modal:justify-end", className),
	...props
});
//#endregion
//#region src/components/AlertModal/AlertModalHeader.tsx
const AlertModalHeader = ({ className, ...props }) => /* @__PURE__ */ jsx("div", {
	className: cn("tw:grid", "tw:grid-cols-[auto_1fr]", "tw:items-start", "tw:gap-y-1", "tw:group-data-[design=default]/alert-modal:gap-y-4", "tw:group-data-[design=argo]/alert-modal:gap-x-3", className),
	...props
});
//#endregion
//#region src/components/AlertModal/AlertModalIcon.tsx
const VARIANT_ICONS = {
	info: /* @__PURE__ */ jsx(Info, {}),
	warning: /* @__PURE__ */ jsx(AlertTriangle, {}),
	danger: /* @__PURE__ */ jsx(AlertTriangle, {}),
	success: /* @__PURE__ */ jsx(CircleCheck, {})
};
const AlertModalIcon = ({ variant, icon }) => {
	const glyph = icon ?? VARIANT_ICONS[variant];
	return /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx("div", {
		"data-slot": "alert-modal-icon",
		className: cn("tw:group-data-[design=argo]/alert-modal:hidden", "tw:absolute", "tw:top-0", "tw:left-8", "tw:translate-y-[-50%]", "tw:size-12"),
		children: /* @__PURE__ */ jsx("div", {
			className: cn("tw:relative", "tw:rounded-full", "tw:size-12", {
				"tw:bg-blue-500": variant === "info",
				"tw:bg-warning-500": variant === "warning",
				"tw:bg-error-500": variant === "danger",
				"tw:bg-green-500": variant === "success"
			}),
			children: /* @__PURE__ */ jsx("span", {
				className: cn("tw:absolute", "tw:inset-0", "tw:flex", "tw:items-center", "tw:justify-center", "tw:text-white", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-6", "tw:[&_svg]:aspect-square"),
				children: glyph
			})
		})
	}), /* @__PURE__ */ jsx("span", {
		"data-slot": "alert-modal-icon",
		className: cn("tw:hidden", "tw:group-data-[design=argo]/alert-modal:inline-flex", "tw:col-start-1", "tw:row-start-1", "tw:self-start", "tw:shrink-0", "tw:items-center", "tw:justify-center", "tw:rounded", "tw:size-8", "tw:[&_svg:not([class*=\"tw:size-\"])]:size-5", {
			"tw:bg-blue-100 tw:text-blue-600": variant === "info",
			"tw:bg-warning-100 tw:text-warning-500": variant === "warning",
			"tw:bg-error-100 tw:text-error-500": variant === "danger",
			"tw:bg-green-100 tw:text-green-900": variant === "success"
		}),
		children: glyph
	})] });
};
//#endregion
//#region src/components/AlertModal/AlertModalOverlay.tsx
const AlertModalOverlay = ({ ref, className, ...props }) => /* @__PURE__ */ jsx(Overlay, {
	...props,
	ref,
	className: cn("tw:fixed", "tw:inset-0", "tw:z-[1051]", "tw:bg-black/50", "tw:data-[state=open]:duration-500", "tw:data-[state=open]:fade-in-0", "tw:data-[state=open]:animate-in", "tw:data-[state=closed]:duration-500", "tw:data-[state=closed]:fade-out-0", "tw:data-[state=closed]:animate-out", className)
});
//#endregion
//#region src/components/AlertModal/AlertModalTitle.tsx
const AlertModalTitle = ({ children, className, ...props }) => /* @__PURE__ */ jsx(Title, {
	...props,
	asChild: true,
	className: cn("tw:font-medium", "tw:text-foreground", "tw:wrap-anywhere", "tw:group-data-[design=default]/alert-modal:border-b", "tw:group-data-[design=default]/alert-modal:border-neutral-150", "tw:group-data-[design=default]/alert-modal:pb-3", className),
	children: /* @__PURE__ */ jsx("h3", { children })
});
//#endregion
//#region src/components/AlertModal/context.ts
const AlertModalContext = createContext(null);
const AlertModalContextProvider = AlertModalContext.Provider;
const useAlertModalContext = () => {
	const context = use(AlertModalContext);
	if (context === null) throw new Error("AlertModal compound components must be rendered inside <AlertModal>.");
	return context;
};
//#endregion
//#region src/components/AlertModal/AlertModalContent.tsx
const AlertModalContent = ({ variant = "info", icon, title, cancelLabel = "Cancel", confirmLabel: actionLabel = "Confirm", confirmDisabled = false, container, className, children, onEscapeKeyDown, ...props }) => {
	const { isLoading, onConfirm, onCancel } = useAlertModalContext();
	const designLanguage = useArgoDesign() ? "argo" : "default";
	const handleEscapeKeyDown = (event) => {
		event.stopPropagation();
		if (isLoading) event.preventDefault();
		onEscapeKeyDown?.(event);
	};
	return /* @__PURE__ */ jsx(Portal, {
		container,
		children: /* @__PURE__ */ jsx(AlertModalOverlay, {
			onClick: (event) => event.stopPropagation(),
			onKeyDown: (event) => {
				if (event.key !== "Escape") event.stopPropagation();
			},
			children: /* @__PURE__ */ jsxs(Content, {
				"data-design": designLanguage,
				...children ? {} : { "aria-describedby": void 0 },
				onEscapeKeyDown: handleEscapeKeyDown,
				className: cn("tailwindcss-preflight", "tw:group/alert-modal", "tw:text-base", "tw:fixed", "tw:left-1/2", "tw:-translate-x-1/2", "tw:z-[1051]", "tw:data-[design=default]:top-20", "tw:data-[design=argo]:top-1/2", "tw:data-[design=argo]:-translate-y-1/2", "tw:w-full", "tw:max-w-[480px]", "tw:max-h-[calc(100vh-2*theme(spacing.20))]", "tw:data-[design=argo]:overflow-y-auto", "tw:flex", "tw:flex-col", "tw:gap-4", "tw:data-[design=default]:rounded-lg", "tw:data-[design=argo]:rounded-3xl", "tw:bg-popover", "tw:text-popover-foreground", "tw:data-[design=default]:px-7", "tw:data-[design=default]:py-8", "tw:data-[design=argo]:p-4", "tw:duration-200", "tw:data-[state=open]:animate-in", "tw:data-[state=open]:fade-in-0", "tw:data-[state=closed]:animate-out", "tw:data-[state=closed]:fade-out-0", "tw:data-[design=default]:data-[state=open]:zoom-in-80", "tw:data-[design=default]:data-[state=open]:slide-in-from-top-[48%]", "tw:data-[design=default]:data-[state=closed]:zoom-out-80", "tw:data-[design=default]:data-[state=closed]:slide-out-to-top-[48%]", "tw:data-[design=argo]:data-[state=open]:zoom-in-95", "tw:data-[design=argo]:data-[state=closed]:zoom-out-95", className),
				...props,
				children: [/* @__PURE__ */ jsxs(AlertModalHeader, { children: [
					icon !== false && /* @__PURE__ */ jsx(AlertModalIcon, {
						variant,
						icon
					}),
					title && /* @__PURE__ */ jsx(AlertModalTitle, {
						className: cn(icon === false ? "tw:col-start-1" : "tw:col-start-2", "tw:row-start-1 tw:self-center"),
						children: title
					}),
					children && /* @__PURE__ */ jsx(AlertModalDescription, {
						className: cn(icon === false ? "tw:col-start-1" : "tw:col-start-2", title ? "tw:row-start-2" : "tw:row-start-1 tw:self-center"),
						children
					})
				] }), /* @__PURE__ */ jsxs(AlertModalFooter, { children: [/* @__PURE__ */ jsx(Cancel, {
					asChild: true,
					children: /* @__PURE__ */ jsx(Button, {
						type: "button",
						variant: "outline",
						disabled: isLoading,
						onClick: onCancel,
						className: "tw:group-data-[design=default]/alert-modal:flex-1",
						children: cancelLabel
					})
				}), /* @__PURE__ */ jsx(Action, {
					asChild: true,
					children: /* @__PURE__ */ jsx(Button, {
						type: "button",
						variant: variant === "danger" ? "danger" : "primary",
						isLoading,
						disabled: isLoading || confirmDisabled,
						onClick: onConfirm,
						className: "tw:group-data-[design=default]/alert-modal:flex-1",
						children: actionLabel
					})
				})] })]
			})
		})
	});
};
//#endregion
//#region src/components/AlertModal/AlertModalTrigger.tsx
const AlertModalTrigger = ({ ref, onClick, onKeyDown, ...props }) => /* @__PURE__ */ jsx(Trigger, {
	...props,
	ref,
	onClick: (event) => {
		event.stopPropagation();
		onClick?.(event);
	},
	onKeyDown: (event) => {
		if (event.key === "Enter" || event.key === " ") event.stopPropagation();
		onKeyDown?.(event);
	}
});
//#endregion
//#region src/components/AlertModal/AlertModal.tsx
const noop = () => void 0;
const AlertModal = ({ children, onConfirm = noop, onCancel = noop, skipAsyncWait = false, isLoading = false, open, defaultOpen, onOpenChange, ...rest }) => {
	const [isConfirming, setIsConfirming] = useState(false);
	const isControlled = open !== void 0;
	const [uncontrolledOpen, setUncontrolledOpen] = useState(defaultOpen ?? false);
	const isOpen = isControlled ? open : uncontrolledOpen;
	const confirmRef = useRef(onConfirm);
	const cancelRef = useRef(onCancel);
	const onOpenChangeRef = useRef(onOpenChange);
	useEffect(() => {
		confirmRef.current = onConfirm;
		cancelRef.current = onCancel;
		onOpenChangeRef.current = onOpenChange;
	}, [
		onConfirm,
		onCancel,
		onOpenChange
	]);
	const setOpen = useCallback((next) => {
		if (!isControlled) setUncontrolledOpen(next);
		onOpenChangeRef.current?.(next);
	}, [isControlled]);
	const context = useMemo(() => ({
		isLoading: isConfirming || isLoading,
		onConfirm: (event) => {
			const result = confirmRef.current(event);
			if (!(result !== null && typeof result === "object" && typeof result.then === "function") || skipAsyncWait) return;
			event.preventDefault();
			setIsConfirming(true);
			Promise.resolve(result).then(() => setOpen(false)).finally(() => setIsConfirming(false));
		},
		onCancel: (event) => {
			cancelRef.current(event);
		}
	}), [
		isConfirming,
		isLoading,
		skipAsyncWait,
		setOpen
	]);
	return /* @__PURE__ */ jsx(Root, {
		...rest,
		open: isOpen,
		onOpenChange: setOpen,
		children: /* @__PURE__ */ jsx(AlertModalContextProvider, {
			value: context,
			children
		})
	});
};
AlertModal.Trigger = AlertModalTrigger;
AlertModal.Content = AlertModalContent;
//#endregion
export { AlertModal };

//# sourceMappingURL=AlertModal-CaQKQ2BX.js.map